import {
  AppUsageLimitConfig,
  AppUsageLimitInfo,
  AppUsageLimits,
  AppUsageType,
  UserAppUsage,
  UserLimits
} from "@/pb/api/devplan/types/usage";
import {AbilityBuilder} from "@casl/ability";
import {Actions, CompanyAbility, CompanySubjects, GlobalAbility} from "@/app/auth/abilities";

export const upgradeRequiredMessage = "[Upgrade Required]"
export const upgradeRequiredHeader = "X-Devplan-Upgrade-Required"

export function defineUserLimitGlobalRules(
  limits: UserLimits, builder: AbilityBuilder<GlobalAbility>,
) {
  const companyCreate = limits.appLimits?.[AppUsageType.CREATE_COMPANY]
  if (companyCreate?.restricted || limits.forcedReadonly) {
    builder.cannot('create', 'Company').because(upgradeRequiredMessage)
    builder.cannot('create', 'PlgOrganization').because(upgradeRequiredMessage)
  }
}

// Extract string literals from CompanySubjects
type CompanySubjectLiterals = Extract<CompanySubjects, string>

// Define the final type as a Record with any CompanySubject as key
const restrictedCompanySubjects: Partial<Record<CompanySubjectLiterals, readonly Actions[]>> = {
  'Document': ['create'],
  'Project': ['create'],
  'UserInvite': ['create'],
  'RepoAnalysis': ['create', 'update'],
  'RepoRescan': ['create', 'update'],
  'SiteRescan': ['create', 'update'],
  'ChatMessage': ['create'],
  'LinearSync': ['create'],
  'JiraSync': ['create'],
  'BitBucketSync': ['create'],
  'ProjectSimulation': ['read'],
  'PromptsDownload': ['read'],
  'PromptsView': ['read'],
  'FeaturesRegeneration': ['create'],
  'PulseEnrollment': ['manage'],
  'CodeResearchEnrollment': ['manage'],
  'RunButton': ['read'],
  'Dashboard': ['create'],
}

type UsageSubjectMapping = CompanySubjectLiterals | readonly CompanySubjectLiterals[]

const typeToSubjects: Partial<Record<AppUsageType, UsageSubjectMapping>> = {
  [AppUsageType.CREATE_DOCUMENT]: 'Document',
  [AppUsageType.CREATE_PROJECT]: ['Project', 'Dashboard'],
  [AppUsageType.INVITE_USER]: 'UserInvite',
  [AppUsageType.CREATE_IDEA]: 'Artifact',
  [AppUsageType.REPOSITORY_ANALYSIS]: 'RepoAnalysis',
  [AppUsageType.REPOSITORY_RESCAN]: 'RepoRescan',
  [AppUsageType.SITE_RESCAN]: 'SiteRescan',
  [AppUsageType.SEND_CHAT_MESSAGE]: 'ChatMessage',
  [AppUsageType.LINEAR_SYNC]: 'LinearSync',
  [AppUsageType.JIRA_SYNC]: 'JiraSync',
  [AppUsageType.BITBUCKET_SYNC]: 'BitBucketSync',
  [AppUsageType.PROJECT_SIMULATION]: 'ProjectSimulation',
  [AppUsageType.ROADMAP_VIEW]: 'RoadmapView',
  [AppUsageType.PROMPTS_DOWNLOADS]: 'PromptsDownload',
  [AppUsageType.VIEW_CODING_PROMPTS]: 'PromptsView',
  [AppUsageType.REGENERATE_FEATURES]: 'FeaturesRegeneration',
  [AppUsageType.PULSE_ENROLLMENT]: 'PulseEnrollment',
  [AppUsageType.CODE_RESEARCH_ENROLLMENT]: 'CodeResearchEnrollment',
  [AppUsageType.RUN_BUTTON_RUNS]: 'RunButton',
}

export function defineUserLimitCompanyRules(
  limits: UserLimits, companyId: number, builder: AbilityBuilder<CompanyAbility>,
) {
  for (const usageType of Object.values(AppUsageType)) {
    //TODO: figure out a better way.
    if (usageType === AppUsageType.UNKNOWN_USAGE) {
      continue
    }
    if (!Number.isInteger(usageType)) {
      continue
    }
    const ut = usageType as AppUsageType
    const subjects = ut in typeToSubjects ? typeToSubjects[ut] : undefined
    if (subjects === undefined) {
      continue
    }
    const limit = limits.appLimits?.[ut]
    if (limits?.forcedReadonly || limit?.restricted) {
      const subjectList: readonly CompanySubjectLiterals[] = typeof subjects === "string" ? [subjects] : subjects
      for (const subj of subjectList) {
        const actions = restrictedCompanySubjects[subj]
        if (actions) {
          actions.forEach(action => builder.cannot(action, subj, {companyId})
            .because(upgradeRequiredMessage))
        }
      }
    }
  }
}

export function getUserLimits(
  usage: UserAppUsage | undefined,
  limits: AppUsageLimits,
  currentTierId: string,
  paid: boolean = false,
  forcedReadonly: boolean = false,
): UserLimits {
  const appLimits: Record<number, AppUsageLimitInfo> = {}
  for (const usageType of Object.values(AppUsageType)) {
    if (usageType === AppUsageType.UNKNOWN_USAGE) {
      continue
    }
    if (!Number.isInteger(usageType)) {
      continue
    }
    appLimits[usageType as AppUsageType] = getLimitInfo(usageType as AppUsageType, usage, limits)
  }
  return {appLimits, currentTierId, paid, forcedReadonly}
}

function getLimitInfo(
  usageType: AppUsageType, usage: UserAppUsage | undefined, limits: AppUsageLimits,
): AppUsageLimitInfo {
  const limit = limits.unlimited ? -1 : fetchLimitByType(usageType, limits.configs)
  const used = fetchUsageByType(usageType, usage?.usage)
  return {limit, usage: used, restricted: limit >= 0 && used >= limit}
}

function fetchLimitByType(usageType: AppUsageType, m: ({ [key: number]: AppUsageLimitConfig }) | undefined) {
  return m ? (m[usageType]?.limit ?? 0) : 0;
}

function fetchUsageByType(usageType: AppUsageType, m: ({ [key: number]: number }) | undefined) {
  return m ? (m[usageType] ?? 0) : 0;
}
